import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { AnalyticsService } from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { AppStateService, WidenService, WindowScrollService } from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { BannerContent, Link, Video } from '@types';
import { Logger } from '@utils/logger';
import { take } from 'rxjs/operators';
import { LinkService } from '@services/link.service';

/**
 * Logger
 */
const logger = Logger.getLogger('BannerComponent');
/**
 * FT Banner component
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `ft-banner`
 *
 * Exported as: `BannerComponent`
 */
@Component({
  selector: 'ft-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent extends AbstractBaseComponent implements OnInit {
  /**
   * Banner Content
   */
  content: BannerContent;

  /**
   * Show Default content
   */
  showDefaultContent: boolean;

  /**
   * Skin type for VI banners
   */
  skin: string;

  /**
   * For analytics event tracking
   */
  event = 'callout_click';
  eventType = 'banner';
  eventName: string;
  linkUrl: string;
  linkText: string;

  disclaimerCaveat: string;
  ribbon: boolean;
  /**
   * Object to be passed to EDS component
   */
  video: Video;

  /**
   * For button style and display label
   */
  bannerBtnLabel: string;
  bannerBtnStyle: string;
  layout: boolean;
  thematic: boolean;
  isHideTitle: boolean;
  signInRequired?: boolean;
  /**
   * Constructor
   */
  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private appStateService: AppStateService,
    private widenService: WidenService,
    private analytics: AnalyticsService,
    private cd: ChangeDetectorRef,
    private caveatService: CaveatService,
    private scrollService: WindowScrollService,
    private ctaLinkService: LinkService
  ) {
    super();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.setBreadcrumbItems();
    // Set content on init
    this.content = this.getContent();
    this.skin = this.getVISkin();
    this.getDisclaimerCaveat();
    this.isRibbonDisplayed();
    this.initVideo();
  }
  /**
   * Init Video
   */
  initVideo() {
    const videoContent = this.getContent();
    this.layout = this.component.getParameters()?.verticalLayout;
    this.thematic = this.component.getParameters()?.thematic;
    this.isHideTitle = this.component.getParameters()?.hideTitle;
    if (videoContent?.video) {
      this.video = this.mapVideoData(videoContent);
    }
  }
  /**
   * Get content from document
   */
  getContent(): BannerContent {
    // Read the content from the resourceapi
    let content;
    content = this.document?.getData<BannerContent>();

    // Do we have a widen image?
    if (content?.widenImage) {
      const widenAssetStr = content.widenImage?.widenAsset;
      if (widenAssetStr) {
        const widenAsset = JSON.parse(widenAssetStr);
        content.imageSrc = this.widenService.getWidenImageVariantUrl(
          widenAsset?.url,
          'original',
          'webp'
        );
      }
    }

    // Do we have a logo image?
    if (content?.logoImage) {
      const widenAssetStr = content.logoImage?.widenAsset;
      if (widenAssetStr) {
        const widenAsset = JSON.parse(widenAssetStr);
        content.logoLink = this.widenService.getWidenImageVariantUrl(
          widenAsset?.url,
          'original',
          'webp'
        );

        // If alt text is null use title
        content.altlogoText =
          content.logoImage?.altText || content.logoImage?.title;
      }
    }

    // if there is internal document link is available.
    if (content?.button) {
      for (const button of content.button) {
        if (button.linkCompound?.linkCollection?.length) {
          button.linkCompound = button.linkCompound.linkCollection[0];
          button.linkCompound.url = this.ctaLinkService.getHref(
            this.page,
            button.linkCompound,
            this.appStateService?.getHomePageUrl()
          );
          button.linkCompound.externalLink = this.getExternalLink(
            button.linkCompound?.external,
            button.linkCompound?.externalSuffix
          );

          button.signInRequired = button.linkCompound?.signInLink;

          // content required for analytic tracking
          this.linkText = button.linkCompound.displayText;
          this.eventName = content.title;
          this.linkUrl = button.linkCompound.url;
          this.signInRequired = button.signInRequired;
        }
      }
    }

    // if there is important information link available for standard banner
    if (content?.importantinformationlink?.linkCollection?.length) {
      content.importantinformationlink =
        content.importantinformationlink.linkCollection[0];
      content.importantinformationlink.url = this.ctaLinkService.getHref(
        this.page,
        content.importantinformationlink,
        this.appStateService?.getHomePageUrl()
      );
    }

    return content;
  }

  /**
   * Wayfinder button click event def.
   */
  wayFinderClicked() {
    this.scrollService.scrollToAnchor('wayfinder');
  }
  /**
   * Set external link class
   * @param external - is link external true|false
   * @param externalSuffix - suffix to add to external link
   */
  private getExternalLink(external: boolean, externalSuffix: string): string {
    if (external) {
      const externalLink = 'external-link';
      if (externalSuffix) {
        // Adding external link suffix
        return externalLink + '--' + externalSuffix;
      }
      return externalLink;
    }
  }

  openLink(inputLink: string) {
    this.windowRef.open(inputLink, '_blank');
  }

  /**
   * Get banner type (theme)
   */
  get type() {
    return this.component.getParameters().type;
  }

  /**
   * get the disclaimer caveate text
   */
  private getDisclaimerCaveat() {
    if (this.displayCaveat()) {
      const caveatOverwriteText = this.getOverwriteCaveat()?.trim();
      if (caveatOverwriteText) {
        this.disclaimerCaveat = '<p>' + caveatOverwriteText + '</p>';
      } else {
        this.caveatService
          .getProximals$('BannerGlobalDisclaimer')
          .pipe(take(1))
          .subscribe((bannerCaveatContext) => {
            this.disclaimerCaveat = bannerCaveatContext[0];
            this.cd.detectChanges();
          });
      }
    }
  }

  /**
   * get skin type for VI banners
   */
  private getVISkin(): string {
    return this.component.getParameters()?.skin;
  }

  /**
   * Get the replacement text of caveat
   * @returns placement value as string
   */
  private getOverwriteCaveat(): string {
    return this.component.getParameters()?.overwriteCaveatText;
  }

  /**
   * Get the status if banner needs to suppress caveat
   * @returns status in boolean
   */
  private displayCaveat(): boolean {
    return this.component.getParameters()?.displayCaveat;
  }
  /**
   * Get the status if banner needs Ribbon
   * @returns status in boolean
   */
  private isRibbonDisplayed() {
    this.ribbon = this.component.getParameters()?.displayRibbon;
  }
  /**
   * Get the Video Data
   */
  private mapVideoData(data) {
    return {
      accountId: data.video.accountId,
      videoId: data.video.videoId,
      playerId: data.video.videoPlayerId,
      mute: this.component.getParameters()?.mute,
      hideControls: this.component.getParameters()?.videoControls,
    };
  }
  /**
   * Track banner click event
   */
  private trackCtaClick(): void {
    this.analytics.trackEvent({
      event: this.event,
      type: this.eventType,
      name: this.eventName,
      link_url: this.linkUrl,
      link_text: this.linkText,
    });
  }

  public processLink(event, linkCompound: Link): void {
    this.trackCtaClick();
    if (this.ctaLinkService.isLinkSignIn(linkCompound)) {
      this.ctaLinkService.openSignInLink(event, linkCompound);
    }
  }
}
